import ls from "local-storage";
import config from "../Config";

export const normalizeRoleNames = (role) => {
  if (!role) {
    return [];
  }

  if (typeof role === "string") {
    const normalizedRole = role.trim();
    return normalizedRole ? [normalizedRole] : [];
  }

  if (Array.isArray(role)) {
    return role
      .flatMap((entry) => normalizeRoleNames(entry))
      .filter((roleName, index, allRoles) => allRoles.indexOf(roleName) === index);
  }

  if (typeof role === "object" && typeof role.name === "string") {
    const normalizedRole = role.name.trim();
    return normalizedRole ? [normalizedRole] : [];
  }

  return [];
};

export const getRoleNamesFromUser = (user) => {
  if (!user || !Array.isArray(user.roles)) {
    return [];
  }

  return user.roles
    .flatMap((role) => normalizeRoleNames(role));
};

export const getPrimaryRoleFromUser = (user) => getRoleNamesFromUser(user)[0] || "";

export const getStoredRoleNames = () => {
  const storedRoles = normalizeRoleNames(ls.get("roles"));
  if (storedRoles.length > 0) {
    return storedRoles;
  }

  return getRoleNamesFromUser(ls.get("user"));
};

export const persistStoredRole = (role) => {
  const normalizedRole = normalizeRoleNames(role)[0] || false;

  ls.set("roles", normalizedRole);

  if (typeof window !== "undefined") {
    if (normalizedRole) {
      window.localStorage.setItem("roles", normalizedRole);
    } else {
      window.localStorage.removeItem("roles");
    }
  }

  return normalizedRole;
};

export const persistStoredUser = (user) => {
  ls.set("user", user || false);
  return user || false;
};

export const syncRoleToggleEligibility = ({ user, role } = {}) => {
  const roleNames = getRoleNamesFromUser(user);
  const currentEligibility = ls.get("roleToggleEligible") === true;
  const nextEligibility =
    currentEligibility ||
    role === "Super Admin" ||
    roleNames.includes("Super Admin") ||
    Number(user?.is_role_toggled) === 1;

  ls.set("roleToggleEligible", nextEligibility);

  if (typeof window !== "undefined") {
    window.localStorage.setItem("roleToggleEligible", String(nextEligibility));
  }

  return nextEligibility;
};

export const canCurrentUserToggleRole = ({ user, role } = {}) => {
  const roleNames = getRoleNamesFromUser(user);

  return (
    ls.get("roleToggleEligible") === true ||
    role === "Super Admin" ||
    roleNames.includes("Super Admin") ||
    Number(user?.is_role_toggled) === 1
  );
};

export const EMS_SERVER_DATE_STORAGE_KEY = "ems_server_date";
export const EMS_SERVER_OFFSET_STORAGE_KEY = "ems_server_time_offset";

export function syncServerTime(serverDate, serverTime, serverTimestamp) {
  if (typeof window === "undefined") return;
  try {
    if (serverDate && /^\d{4}-\d{2}-\d{2}$/.test(String(serverDate).trim())) {
      window.localStorage.setItem(EMS_SERVER_DATE_STORAGE_KEY, String(serverDate).trim());
    }
    if (serverTimestamp && Number(serverTimestamp) > 0) {
      const serverMs = Number(serverTimestamp) * 1000;
      const offset = serverMs - Date.now();
      window.localStorage.setItem(EMS_SERVER_OFFSET_STORAGE_KEY, String(offset));
    } else if (serverDate && serverTime) {
      const serverIso = `${serverDate}T${serverTime}+05:30`;
      const serverMs = new Date(serverIso).getTime();
      if (!isNaN(serverMs)) {
        const offset = serverMs - Date.now();
        window.localStorage.setItem(EMS_SERVER_OFFSET_STORAGE_KEY, String(offset));
      }
    }
  } catch (e) {}
}

export function getLocalDateString(d = new Date()) {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function getServerNow() {
  const now = Date.now();
  if (typeof window === "undefined") return new Date(now);
  try {
    const rawOffset = window.localStorage.getItem(EMS_SERVER_OFFSET_STORAGE_KEY);
    if (rawOffset !== null) {
      const offset = Number(rawOffset);
      if (!isNaN(offset)) {
        const clientDate = getLocalDateString();
        const storedServerDate = window.localStorage.getItem(EMS_SERVER_DATE_STORAGE_KEY);
        // If client date matches server date, any massive >12h offset was from pre-NTP sync. Clear it!
        if (clientDate === storedServerDate && Math.abs(offset) > 12 * 60 * 60 * 1000) {
          window.localStorage.removeItem(EMS_SERVER_OFFSET_STORAGE_KEY);
          return new Date(now);
        }
        return new Date(now + offset);
      }
    }
  } catch (e) {}
  return new Date(now);
}

export function getTodayDateString() {
  if (typeof window !== "undefined") {
    try {
      const storedServerDate = window.localStorage.getItem(EMS_SERVER_DATE_STORAGE_KEY);
      const clientLocalDate = getLocalDateString();

      // If we have a verified server date from the Laravel API:
      if (storedServerDate && /^\d{4}-\d{2}-\d{2}$/.test(storedServerDate.trim())) {
        const cleanServerDate = storedServerDate.trim();
        // Scenario A: Client PC clock is lagging behind server (CMOS battery lag showing yesterday),
        // or client clock has now caught up with server date:
        // In both cases, clientLocalDate <= cleanServerDate, and the server date is authoritative!
        if (clientLocalDate <= cleanServerDate) {
          return cleanServerDate;
        }
        // Scenario B: Real next day has genuinely arrived on the client (clientLocalDate > cleanServerDate):
        return clientLocalDate;
      }
    } catch (e) {}
  }
  return getLocalDateString();
}

export const DEFAULT_CUTOFF_HOUR = 19; // 7:00 PM
export const DEFAULT_CUTOFF_MINUTE = 0;
export const DEFAULT_CUTOFF_TIME_STRING = "19:00"; // 7:00 PM
export const TAB_CLOSE_CUTOFF_STORAGE_KEY = "ems_tab_close_cutoff_time";
export const TAB_CLOSE_CUTOFF_EVENT = "ems-tab-close-cutoff-updated";

export function formatTo12Hour(hour, minute) {
  const h = Number(hour) || 0;
  const m = Number(minute) || 0;
  const period = h >= 12 ? "PM" : "AM";
  const displayHour = h % 12 === 0 ? 12 : h % 12;
  const displayMinute = String(m).padStart(2, "0");
  const displayHourStr = String(displayHour).padStart(2, "0");
  return `${displayHourStr}:${displayMinute} ${period}`;
}

export function parse12HourTime(time12Str) {
  if (!time12Str || typeof time12Str !== "string") return null;
  const match = time12Str.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
  if (!match) return null;
  let [, hStr, mStr, period] = match;
  let h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  if (period) {
    const periodUpper = period.toUpperCase();
    if (periodUpper === "PM" && h < 12) h += 12;
    if (periodUpper === "AM" && h === 12) h = 0;
  }
  if (h >= 0 && h < 24 && m >= 0 && m < 60) {
    const hh = String(h).padStart(2, "0");
    const mm = String(m).padStart(2, "0");
    return {
      hour: h,
      minute: m,
      timeString: `${hh}:${mm}`,
      formatted12: formatTo12Hour(h, m),
    };
  }
  return null;
}

export function getTabCloseCutoffTime() {
  if (typeof window === "undefined") {
    return {
      hour: DEFAULT_CUTOFF_HOUR,
      minute: DEFAULT_CUTOFF_MINUTE,
      timeString: DEFAULT_CUTOFF_TIME_STRING,
      formatted12: formatTo12Hour(DEFAULT_CUTOFF_HOUR, DEFAULT_CUTOFF_MINUTE),
    };
  }

  try {
    const stored = window.localStorage.getItem(TAB_CLOSE_CUTOFF_STORAGE_KEY);
    if (stored) {
      if (/^\d{1,2}:\d{2}$/.test(stored.trim())) {
        const [h, m] = stored.trim().split(":").map(Number);
        if (!isNaN(h) && !isNaN(m) && h >= 0 && h < 24 && m >= 0 && m < 60) {
          return {
            hour: h,
            minute: m,
            timeString: `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`,
            formatted12: formatTo12Hour(h, m),
          };
        }
      }
      const parsed12 = parse12HourTime(stored);
      if (parsed12) return parsed12;
    }
  } catch (e) {}

  // By default: 7:00 PM (19:00)
  return {
    hour: DEFAULT_CUTOFF_HOUR,
    minute: DEFAULT_CUTOFF_MINUTE,
    timeString: DEFAULT_CUTOFF_TIME_STRING,
    formatted12: formatTo12Hour(DEFAULT_CUTOFF_HOUR, DEFAULT_CUTOFF_MINUTE),
  };
}

export function saveTabCloseCutoffTime(timeValue) {
  let timeObj = null;
  if (typeof timeValue === "string") {
    if (/^\d{1,2}:\d{2}$/.test(timeValue.trim())) {
      const [h, m] = timeValue.trim().split(":").map(Number);
      if (!isNaN(h) && !isNaN(m) && h >= 0 && h < 24 && m >= 0 && m < 60) {
        timeObj = {
          hour: h,
          minute: m,
          timeString: `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`,
          formatted12: formatTo12Hour(h, m),
        };
      }
    } else {
      timeObj = parse12HourTime(timeValue);
    }
  } else if (typeof timeValue === "object" && timeValue !== null) {
    const h = Number(timeValue.hour);
    const m = Number(timeValue.minute);
    if (!isNaN(h) && !isNaN(m) && h >= 0 && h < 24 && m >= 0 && m < 60) {
      timeObj = {
        hour: h,
        minute: m,
        timeString: `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`,
        formatted12: formatTo12Hour(h, m),
      };
    }
  }

  if (!timeObj) {
    timeObj = {
      hour: DEFAULT_CUTOFF_HOUR,
      minute: DEFAULT_CUTOFF_MINUTE,
      timeString: DEFAULT_CUTOFF_TIME_STRING,
      formatted12: formatTo12Hour(DEFAULT_CUTOFF_HOUR, DEFAULT_CUTOFF_MINUTE),
    };
  }

  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(TAB_CLOSE_CUTOFF_STORAGE_KEY, timeObj.timeString);
      window.dispatchEvent(
        new CustomEvent(TAB_CLOSE_CUTOFF_EVENT, { detail: timeObj })
      );
    } catch (e) {}
  }

  return timeObj;
}

export const AUTO_LOGOUT_CUTOFF_HOUR = DEFAULT_CUTOFF_HOUR;
export const AUTO_LOGOUT_CUTOFF_MINUTE = DEFAULT_CUTOFF_MINUTE;
export const TAB_HEARTBEAT_STORAGE_KEY = "ems_open_tabs";
export const TAB_HEARTBEAT_TIMEOUT_MS = 3000;

export function isAfterCutoffTime(cutoffHour, cutoffMinute) {
  if (typeof window === "undefined") return false;
  const cutoff = getTabCloseCutoffTime();
  const targetHour = typeof cutoffHour === "number" ? cutoffHour : cutoff.hour;
  const targetMinute = typeof cutoffMinute === "number" ? cutoffMinute : cutoff.minute;
  const now = getServerNow();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const cutoffTotalMinutes = targetHour * 60 + targetMinute;
  return currentMinutes >= cutoffTotalMinutes;
}

export function isPageReload() {
  if (typeof window === "undefined") return false;
  try {
    const navEntries = window.performance?.getEntriesByType?.("navigation");
    if (navEntries && navEntries.length > 0) {
      return navEntries[0].type === "reload";
    }
    if (window.performance?.navigation?.type === 1) {
      return true;
    }
  } catch (e) {}
  return false;
}

export function getTabId() {
  if (typeof window === "undefined") return "server";
  let tabId = window.sessionStorage.getItem("ems_tab_id");
  if (!tabId) {
    tabId = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 8)}`;
    try {
      window.sessionStorage.setItem("ems_tab_id", tabId);
    } catch (e) {}
  }
  return tabId;
}

export function getActiveTabsRegistry() {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(TAB_HEARTBEAT_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    return {};
  }
}

export function getLocalTimeString(date = new Date()) {
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  return `${hours}:${minutes}:${seconds}`;
}

export function updateTabHeartbeat() {
  if (typeof window === "undefined") return;
  try {
    const rawToken = ls.get("authToken") || window.localStorage.getItem("authToken");
    if (!rawToken || rawToken === false || rawToken === "false" || rawToken === "null" || rawToken === "undefined") {
      removeTabFromRegistry();
      return;
    }
    const tabId = getTabId();
    const tabs = getActiveTabsRegistry();
    const now = Date.now();
    tabs[tabId] = now;
    // Clean up stale tab heartbeats older than 4 seconds
    Object.keys(tabs).forEach((tid) => {
      if (tid !== tabId && now - Number(tabs[tid]) > 4000) {
        delete tabs[tid];
      }
    });
    window.localStorage.setItem(TAB_HEARTBEAT_STORAGE_KEY, JSON.stringify(tabs));
    window.localStorage.setItem("ems_last_active_time", getLocalTimeString(new Date(now)));
    window.localStorage.setItem("ems_last_active_date", getTodayDateString());
  } catch (e) {}
}

export function removeTabFromRegistry() {
  if (typeof window === "undefined") return;
  try {
    const tabId = getTabId();
    const tabs = getActiveTabsRegistry();
    delete tabs[tabId];
    window.localStorage.setItem(TAB_HEARTBEAT_STORAGE_KEY, JSON.stringify(tabs));
  } catch (e) {}
}

export function hasOtherActiveTab() {
  if (typeof window === "undefined") return false;
  try {
    const tabId = getTabId();
    const tabs = getActiveTabsRegistry();
    const now = Date.now();
    return Object.keys(tabs).some(
      (tid) => tid !== tabId && now - Number(tabs[tid]) <= TAB_HEARTBEAT_TIMEOUT_MS
    );
  } catch (e) {
    return false;
  }
}

let heartbeatTimer = null;
export function startTabHeartbeat() {
  if (typeof window === "undefined") return;
  updateTabHeartbeat();
  if (!heartbeatTimer) {
    heartbeatTimer = setInterval(updateTabHeartbeat, 1500);
  }
}

export function stopTabHeartbeat() {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
  removeTabFromRegistry();
}

export async function directApiClockOut(token) {
  if (!token || token === "false" || token === "null" || String(token).trim() === "") return;
  try {
    const url = config.apiurl + config.apis.userOutTime;
    const formData = new FormData();
    formData.append("outTime", "outtimedata");

    const customOutTime =
      (typeof window !== "undefined" && (window.localStorage.getItem("ems_last_closed_time") || window.localStorage.getItem("ems_last_active_time"))) ||
      null;
    const customOutDate =
      (typeof window !== "undefined" && (window.localStorage.getItem("ems_last_closed_date") || window.localStorage.getItem("ems_last_active_date"))) ||
      getTodayDateString();

    if (customOutTime) {
      formData.append("custom_out_time", customOutTime);
    }
    if (customOutDate) {
      formData.append("custom_out_date", customOutDate);
    }

    if (typeof window !== "undefined" && typeof window.fetch === "function") {
      await window.fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
        body: formData,
      });
    }
  } catch (e) {}

  try {
    const logoutUrl = config.apiurl + config.authApis.logout;
    if (typeof window !== "undefined" && typeof window.fetch === "function") {
      await window.fetch(logoutUrl, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    }
  } catch (e) {}
}

export async function performSessionExpiryLogout() {
  const rawToken =
    ls.get("authToken") ||
    (typeof window !== "undefined" ? window.localStorage.getItem("authToken") : null);

  if (rawToken && rawToken !== "false" && rawToken !== "null" && String(rawToken).trim() !== "") {
    await directApiClockOut(rawToken);
  }

  clearClientAuthState();
}

if (typeof window !== "undefined") {
  const handleTabClose = () => {
    try {
      const now = new Date();
      window.localStorage.setItem("ems_last_closed_time", getLocalTimeString(now));
      window.localStorage.setItem("ems_last_closed_date", getTodayDateString());
      window.localStorage.setItem("ems_last_closed_timestamp", Date.now().toString());
      removeTabFromRegistry();
    } catch (e) {}
  };
  window.addEventListener("beforeunload", handleTabClose);
  window.addEventListener("pagehide", handleTabClose);

  // If this tab already has an active session from today, resume heartbeat
  const today = getTodayDateString();
  const sessionActive = window.sessionStorage.getItem("ems_session_active") === "true";
  const sessionDate = window.sessionStorage.getItem("ems_session_date");
  if (sessionActive && sessionDate === today) {
    startTabHeartbeat();
  }
}

export function clearClientAuthState(broadcast = true) {
  ls.set("authToken", false);
  ls.set("isActive", false);
  ls.set("roles", false);
  ls.set("user", false);
  ls.set("userAccess", []);

  if (typeof window !== "undefined") {
    try {
      const existingSnapshot = JSON.parse(window.localStorage.getItem("ems_timer_snapshot") || "{}");
      const today = getTodayDateString();
      const preservedTotal =
        Number(window.localStorage.getItem("ems_last_active_totaltimer")) ||
        Number(existingSnapshot?.totaltimer) ||
        0;

      window.localStorage.removeItem("authToken");
      window.localStorage.removeItem("roles");
      window.localStorage.removeItem("user");
      window.localStorage.removeItem("userAccess");
      window.localStorage.removeItem("tokenRefreshed");
      window.localStorage.removeItem("ems_login_date");
      window.localStorage.removeItem("ems_login_timestamp");
      window.localStorage.removeItem("ems_auto_timer_session_started_at");
      window.localStorage.removeItem("ems_last_closed_time");
      window.localStorage.removeItem("ems_last_closed_date");
      window.localStorage.removeItem("ems_last_closed_timestamp");
      window.localStorage.removeItem(EMS_SERVER_DATE_STORAGE_KEY);
      window.localStorage.removeItem(EMS_SERVER_OFFSET_STORAGE_KEY);
      window.localStorage.removeItem("ems_login_server_timestamp");

      window.localStorage.setItem("ems_timer_snapshot", JSON.stringify({
        timer: 0,
        totaltimer: preservedTotal,
        isActive: false,
        savedAt: Date.now(),
        dateKey: today,
      }));

      window.sessionStorage.removeItem("ems_session_active");
      window.sessionStorage.removeItem("ems_session_date");
      window.sessionStorage.removeItem("ems_tab_id");
    } catch (err) {}

    stopTabHeartbeat();

    if (broadcast && sessionSyncChannel) {
      try {
        sessionSyncChannel.postMessage({ type: "SESSION_FORCE_LOGOUT" });
      } catch (e) {}
    }
  }
}

let sessionSyncChannel = null;
if (typeof window !== "undefined" && typeof window.BroadcastChannel !== "undefined") {
  try {
    sessionSyncChannel = new BroadcastChannel("ems_tab_session_channel");
    sessionSyncChannel.onmessage = (event) => {
      const { type, date } = event?.data || {};
      const today = getTodayDateString();
      if (type === "SESSION_PING") {
        const isActive = window.sessionStorage.getItem("ems_session_active") === "true";
        const sessDate = window.sessionStorage.getItem("ems_session_date");
        if (isActive && sessDate === today) {
          sessionSyncChannel.postMessage({ type: "SESSION_PONG", date: today });
        }
      } else if (type === "SESSION_PONG") {
        if (date === today) {
          window.sessionStorage.setItem("ems_session_active", "true");
          window.sessionStorage.setItem("ems_session_date", today);
          startTabHeartbeat();
        }
      } else if (type === "SESSION_FORCE_LOGOUT") {
        clearClientAuthState(false);
        if (window.location && window.location.pathname !== "/login") {
          window.location.replace("/login");
        }
      }
    };

    // If this tab doesn't have an active session marker yet, ask other open tabs
    const hasActiveSess = window.sessionStorage.getItem("ems_session_active") === "true";
    const sessDate = window.sessionStorage.getItem("ems_session_date");
    if (!hasActiveSess || sessDate !== getTodayDateString()) {
      sessionSyncChannel.postMessage({ type: "SESSION_PING" });
    }
  } catch (e) {}
}

export const recordLoginSession = (serverDate, serverTime, serverTimestamp) => {
  if (serverDate || serverTimestamp) {
    syncServerTime(serverDate, serverTime, serverTimestamp);
  }
  const today = (serverDate && /^\d{4}-\d{2}-\d{2}$/.test(String(serverDate).trim()))
    ? String(serverDate).trim()
    : getTodayDateString();
  const now = Date.now().toString();
  if (typeof window !== "undefined") {
    try {
      window.sessionStorage.setItem("ems_session_active", "true");
      window.sessionStorage.setItem("ems_session_date", today);
      window.localStorage.setItem("ems_login_date", today);
      window.localStorage.setItem("ems_login_timestamp", now);
      if (serverTimestamp && Number(serverTimestamp) > 0) {
        window.localStorage.setItem("ems_login_server_timestamp", String(Number(serverTimestamp) * 1000));
      }
      startTabHeartbeat();
    } catch (e) {}
  }
};

export const isLoginSessionExpired = () => {
  if (typeof window === "undefined") {
    return false;
  }

  const rawToken = ls.get("authToken") || window.localStorage.getItem("authToken");
  const hasToken = Boolean(
    rawToken &&
    rawToken !== false &&
    rawToken !== "false" &&
    rawToken !== "null" &&
    rawToken !== "undefined" &&
    String(rawToken).trim() !== ""
  );

  if (!hasToken) {
    return true;
  }

  const today = getTodayDateString();
  const storedLoginDate = window.localStorage.getItem("ems_login_date");

  // Rule 1: Overnight expiry (tomorrow / "kale")
  // If login date is recorded and doesn't match today:
  if (storedLoginDate && storedLoginDate !== today) {
    const loginTs = Number(window.localStorage.getItem("ems_login_timestamp")) || 0;
    const elapsedClientMs = Date.now() - loginTs;

    const loginServerTs = Number(window.localStorage.getItem("ems_login_server_timestamp")) || 0;
    const currentServerMs = getServerNow().getTime();
    const elapsedServerMs = loginServerTs > 0 ? (currentServerMs - loginServerTs) : elapsedClientMs;

    // If login occurred recently (within 2 hours) or clock jumped backwards:
    // Update login date to today instead of false logout
    if (loginTs > 0 && (elapsedServerMs < 2 * 60 * 60 * 1000 || elapsedClientMs < 0 || elapsedServerMs < 0)) {
      window.localStorage.setItem("ems_login_date", today);
      window.sessionStorage.setItem("ems_session_date", today);
      return false;
    }
    return true;
  }

  // Rule 2: Page Reload (F5 / Ctrl+R / Refresh)
  // A page reload within the same tab is NEVER a tab close!
  if (isPageReload()) {
    window.sessionStorage.setItem("ems_session_active", "true");
    window.sessionStorage.setItem("ems_session_date", today);
    updateTabHeartbeat();
    startTabHeartbeat();
    return false;
  }

  const afterCutoff = isAfterCutoffTime();
  const sessionActive = window.sessionStorage.getItem("ems_session_active") === "true";
  const sessionDate = window.sessionStorage.getItem("ems_session_date");
  const hasOtherTab = hasOtherActiveTab();

  // If this tab already has an active session from today
  if (sessionActive && sessionDate === today) {
    updateTabHeartbeat();
    startTabHeartbeat();
    return false;
  }

  // If this tab does not have an active session marker (new tab opened):
  // Check if another HRMS tab is currently open and active
  if (hasOtherTab) {
    // Another HRMS tab is open! Share active session to this new tab.
    window.sessionStorage.setItem("ems_session_active", "true");
    window.sessionStorage.setItem("ems_session_date", today);
    updateTabHeartbeat();
    startTabHeartbeat();
    return false;
  }

  // If we reach here, this tab has no active session AND no other HRMS tab is open.
  // That means: HRMS was closed (tab closed or browser closed) and reopened!
  // If it is after 11:30 AM (cutoff):
  if (afterCutoff) {
    return true;
  }

  // If it is before 11:30 AM:
  // Tab/browser close before 11:30 AM allows continuing today's session.
  if (storedLoginDate === today || !storedLoginDate) {
    if (!storedLoginDate) {
      window.localStorage.setItem("ems_login_date", today);
    }
    window.sessionStorage.setItem("ems_session_active", "true");
    window.sessionStorage.setItem("ems_session_date", today);
    updateTabHeartbeat();
    startTabHeartbeat();
    return false;
  }

  return false;
};
